import { Vec3, vec3, add, sub, scale, cross, length, normalize, ZERO } from '../math/vec3'
import { rotate, unrotate } from '../math/quat'
import { clamp } from '../math/scalar'
import { CelestialBody } from '../world/body'
import { pressureRatio, densityAt } from '../world/atmosphere'
import { Vessel, partById } from '../vessel/vessel'
import { PartCatalog, partDef } from '../vessel/catalog'
import { MassProperties } from '../vessel/massProperties'
import { fuelPathForEngine, availablePropellant, drawPropellant } from '../vessel/propellant'
import {
  GimbalCommand, thrustAtPressure, ispAtPressure, massFlow, thrustDirection,
} from '../propulsion/engine'
import { aeroForces, totalAeroForce, aeroTorque } from '../aero/forces'
import { PuntoContatto, forzeDiContatto, ParametriContatto } from '../contact/ground'
import { Paracadute, forzeParacadute } from '../aero/parachute'
import { RigidState, Wrench, InertialProperties, inertialProperties, stepRK4 } from './rigidBody'

export interface FlightControls {
  /** manetta in [0,1] */
  readonly throttle: number
  readonly gimbal: GimbalCommand
}

export interface FlightContext {
  readonly vessel: Vessel
  readonly catalog: PartCatalog
  readonly body: CelestialBody
  readonly controls: FlightControls
  /** id dei motori accesi in questo stadio */
  readonly activeEngines: readonly string[]
  /** piedi che possono toccare terra; se manca, il veicolo non ha gambe */
  readonly contatti?: readonly PuntoContatto[]
  readonly parametriContatto?: ParametriContatto
  readonly paracadute?: readonly Paracadute[]
}

export interface EngineOutput {
  readonly partId: string
  /** spinta effettiva, N */
  readonly thrust: number
  /** portata di propellente, kg/s */
  readonly flow: number
  /** versore della spinta in sistema veicolo */
  readonly direction: Vec3
  /** punto d'applicazione in sistema veicolo */
  readonly point: Vec3
}

export const altitudeOf = (position: Vec3, body: CelestialBody): number =>
  length(position) - body.radius

/** Accelerazione di gravita: a = −μ r̂ / |r|². */
export function gravityAcceleration(position: Vec3, body: CelestialBody): Vec3 {
  const r2 = position.x ** 2 + position.y ** 2 + position.z ** 2
  if (r2 <= 0) return ZERO
  return scale(normalize(position), -body.mu / r2)
}

/**
 * Velocita dell'aria trascinata dalla rotazione del pianeta.
 * Il polo nord del corpo centrale e +Z.
 */
export function atmosphereVelocity(position: Vec3, body: CelestialBody): Vec3 {
  if (body.rotationPeriod <= 0) return ZERO
  const omega = vec3(0, 0, (2 * Math.PI) / body.rotationPeriod)
  return cross(omega, position)
}

/** Spinta e portata di ogni motore acceso, alla pressione data. */
export function engineOutputs(ctx: FlightContext, pRatio: number): EngineOutput[] {
  const throttle = clamp(ctx.controls.throttle, 0, 1)
  const out: EngineOutput[] = []

  for (const id of ctx.activeEngines) {
    const part = partById(ctx.vessel, id)
    const def = partDef(ctx.catalog, part.defId).engine
    if (!def) continue

    const path = fuelPathForEngine(ctx.vessel, ctx.catalog, id)
    const secco = availablePropellant(ctx.vessel, path) <= 0
    const level = def.throttleable ? throttle : 1
    const thrust = secco ? 0 : thrustAtPressure(def, pRatio) * level
    const isp = ispAtPressure(def, pRatio)

    out.push({
      partId: id,
      thrust,
      flow: massFlow(thrust, isp),
      direction: thrustDirection(part, def, ctx.controls.gimbal),
      point: part.position,
    })
  }
  return out
}

/**
 * Somma di gravita, spinta e aerodinamica.
 * La forza esce in sistema MONDO, la coppia in sistema CORPO: e la convenzione
 * che si aspetta stepRK4.
 */
export function vesselWrench(
  ctx: FlightContext,
  state: RigidState,
  com: Vec3,
  engines: readonly EngineOutput[],
  mass: number,
): Wrench {
  // gravita: agisce nel centro di massa, nessuna coppia
  let forceWorld = scale(gravityAcceleration(state.position, ctx.body), mass)
  let torqueBody = ZERO

  // spinta
  let thrustBody = ZERO
  for (const e of engines) {
    if (e.thrust <= 0) continue
    const f = scale(e.direction, e.thrust)
    thrustBody = add(thrustBody, f)
    torqueBody = add(torqueBody, cross(sub(e.point, com), f))
  }
  forceWorld = add(forceWorld, rotate(state.orientation, thrustBody))

  // aerodinamica
  const altitude = altitudeOf(state.position, ctx.body)
  const density = densityAt(ctx.body.atmosphere, altitude)
  if (density > 0) {
    const airWorld = sub(atmosphereVelocity(state.position, ctx.body), state.velocity)
    const airBody = unrotate(state.orientation, airWorld)
    const forces = aeroForces(ctx.vessel, ctx.catalog, airBody, density)
    forceWorld = add(forceWorld, rotate(state.orientation, totalAeroForce(forces)))
    torqueBody = add(torqueBody, aeroTorque(forces, com))

    // i paracadute: resistenza enorme applicata in alto, che e anche il
    // motivo per cui una capsula appesa a un paracadute si raddrizza da sola
    if (ctx.paracadute && ctx.paracadute.length > 0) {
      for (const f of forzeParacadute(ctx.paracadute, ctx.vessel, ctx.catalog, airBody, ctx.body, altitude)) {
        forceWorld = add(forceWorld, rotate(state.orientation, f.force))
        torqueBody = add(torqueBody, cross(sub(f.point, com), f.force))
      }
    }
  }

  // contatto col suolo: molla-smorzatore ai piedi delle gambe
  if (ctx.contatti && ctx.contatti.length > 0) {
    const contatti = forzeDiContatto(
      ctx.contatti, state, { mass, com, inertia: [] }, ctx.body, ctx.parametriContatto,
    )
    let contattoBody: Vec3 = ZERO
    for (const c of contatti) {
      contattoBody = add(contattoBody, c.force)
      torqueBody = add(torqueBody, cross(sub(c.point, com), c.force))
    }
    forceWorld = add(forceWorld, rotate(state.orientation, contattoBody))
  }

  return { force: forceWorld, torque: torqueBody }
}

export interface FlightStepResult {
  readonly state: RigidState
  readonly propellantUsed: number
  /** true se almeno un motore ha chiesto piu propellente di quanto ce n'era */
  readonly flameout: boolean
}

/**
 * Un passo di simulazione.
 *
 * Massa e inerzia restano costanti dentro il passo e il propellente viene
 * scalato una volta sola alla fine: valutarlo quattro volte dentro RK4
 * significherebbe consumare carburante nelle valutazioni intermedie, che sono
 * tentativi, non tempo trascorso.
 */
export function stepFlight(
  ctx: FlightContext,
  state: RigidState,
  massProps: MassProperties,
  t: number,
  dt: number,
): FlightStepResult {
  const pRatio = pressureRatio(ctx.body.atmosphere, altitudeOf(state.position, ctx.body))
  const engines = engineOutputs(ctx, pRatio)
  const props: InertialProperties = inertialProperties(massProps.mass, massProps.inertia)

  const next = stepRK4(
    state,
    props,
    (s) => vesselWrench(ctx, s, massProps.com, engines, massProps.mass),
    t,
    dt,
  )

  let used = 0
  let flameout = false
  for (const e of engines) {
    if (e.flow <= 0) continue
    const path = fuelPathForEngine(ctx.vessel, ctx.catalog, e.partId)
    const richiesto = e.flow * dt
    const preso = drawPropellant(ctx.vessel, path, richiesto)
    used += preso
    if (preso < richiesto - 1e-12) flameout = true
  }

  return { state: next, propellantUsed: used, flameout }
}
